import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import logo from '../assets/logo.png';

export default function Header() {
  const { totalItems } = useCart();
  const { user, logout, isAuthenticated } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setAccountOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!accountOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(event.target as Node)) {
        setAccountOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [accountOpen]);

  const firstName = user?.name.split(' ')[0];

  return (
    <header className="header">
      <div className="container header-inner">
        <Link to="/" className="logo" aria-label="HealthShopLongevity ana sayfa">
          <span className="logo-icon"><img src={logo} width={300} height={90} /></span>
        </Link>

        <nav className="nav" aria-label="Ana menü">
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
            Ana Sayfa
          </NavLink>
          <NavLink to="/shop" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
            Ürünler
          </NavLink>
          <NavLink to="/blog" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
            Blog
          </NavLink>
        </nav>

        <div className="header-actions">
          {isAuthenticated ? (
            <div className="user-menu" ref={accountRef}>
              <button
                type="button"
                className="btn-auth btn-auth-outline account-menu-btn"
                aria-expanded={accountOpen}
                aria-haspopup="true"
                onClick={() => setAccountOpen((open) => !open)}
              >
                <span className="account-menu-btn-label">
                  Hesap
                  {firstName && <span className="account-menu-btn-name"> · {firstName}</span>}
                </span>
                <svg
                  className={`account-menu-chevron${accountOpen ? ' is-open' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {accountOpen && (
                <div className="account-menu-panel" role="menu">
                  {firstName && (
                    <p className="account-menu-greeting">Merhaba, {firstName}</p>
                  )}
                  {user?.role === 'admin' && (
                    <NavLink
                      to="/admin"
                      role="menuitem"
                      className={({ isActive }) =>
                        isActive ? 'account-menu-item active' : 'account-menu-item'
                      }
                    >
                      Yönetim
                    </NavLink>
                  )}
                  <NavLink
                    to="/orders"
                    role="menuitem"
                    className={({ isActive }) =>
                      isActive ? 'account-menu-item active' : 'account-menu-item'
                    }
                  >
                    Siparişlerim
                  </NavLink>
                  <NavLink
                    to="/pre-orders"
                    role="menuitem"
                    className={({ isActive }) =>
                      isActive ? 'account-menu-item active' : 'account-menu-item'
                    }
                  >
                    Ön Siparişlerim
                  </NavLink>
                  <NavLink
                    to="/profile"
                    role="menuitem"
                    className={({ isActive }) =>
                      isActive ? 'account-menu-item active' : 'account-menu-item'
                    }
                  >
                    Profilim
                  </NavLink>
                  <NavLink
                    to="/addresses"
                    role="menuitem"
                    className={({ isActive }) =>
                      isActive ? 'account-menu-item active' : 'account-menu-item'
                    }
                  >
                    Adreslerim
                  </NavLink>
                  <button
                    type="button"
                    role="menuitem"
                    className="account-menu-item account-menu-item-logout"
                    onClick={() => void logout()}
                  >
                    Çıkış
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-links">
              <Link to="/login" className="btn-auth btn-auth-outline">
                Giriş
              </Link>
              <Link to="/register" className="btn-auth btn-auth-primary">
                <span className="btn-auth-label-full">Kayıt Ol</span>
                <span className="btn-auth-label-short">Kayıt</span>
              </Link>
            </div>
          )}

          <Link to="/cart" className="cart-btn" aria-label={`Sepet${totalItems > 0 ? `, ${totalItems} ürün` : ''}`}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
          </Link>
        </div>
      </div>
    </header>
  );
}
